<template>
  <div class="quit-site-form">
    <div class="quit-site-form__header">
      <Button
        icon="far fa-arrow-left"
        class="p-button-text p-button-rounded"
        @click="$emit('cancel')"
      />
      <div class="quit-site-form__title">
        <h2>{{ t('flows.operators.quitSite.label') }}</h2>
        <span class="quit-site-form__subtitle">
          {{ t('flows.operatorKinds.trigger') }}
        </span>
      </div>
      <Button
        icon="far fa-times"
        class="p-button-text p-button-rounded"
        @click="$emit('close')"
      />
    </div>

    <div class="quit-site-form__content">
      <div class="quit-site-form__field">
        <label class="quit-site-form__label">
          {{ t('flows.operators.quitSite.userType') }}
        </label>
        <div class="quit-site-form__options">
          <div
            v-for="option in options"
            :key="option.value"
            class="quit-site-form__option"
            :class="{ 'quit-site-form__option--selected': localData.multiplebox_type === option.value }"
            @click="localData.multiplebox_type = option.value"
          >
            <i :class="option.icon" />
            <span>{{ option.label }}</span>
          </div>
        </div>
        <small
          v-if="formValidation?.multiplebox_type?.$errors?.length"
          class="p-error"
        >
          {{ formValidation.multiplebox_type.$errors[0].$message }}
        </small>
      </div>
    </div>

    <div class="quit-site-form__footer">
      <Button
        :label="t('flows.operatorPanel.cancel')"
        class="p-button-text"
        @click="$emit('cancel')"
      />
      <Button
        :label="t('flows.operatorPanel.addStep')"
        class="p-button-success"
        @click="$emit('submit')"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import type { QuitSiteData } from './index';

export default defineComponent({
  name: 'QuitSiteForm',

  components: {
    Button,
  },

  props: {
    modelValue: {
      type: Object as PropType<QuitSiteData>,
      default: () => ({ multiplebox_type: '0' }),
    },
    formValidation: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: ['update:modelValue', 'cancel', 'close', 'submit'],

  setup(props, { emit }) {
    const { t } = useI18n();

    const localData = ref<QuitSiteData>({ ...props.modelValue });

    const options = [
      {
        value: '0',
        label: t('flows.operators.quitSite.hasQuit'),
        icon: 'far fa-sign-out',
      },
      {
        value: '1',
        label: t('flows.operators.quitSite.isGoingToQuit'),
        icon: 'far fa-hand',
      },
    ];

    watch(localData, (newData) => {
      emit('update:modelValue', newData);
    }, { deep: true });

    watch(() => props.modelValue, (newValue) => {
      if (newValue !== localData.value) {
        localData.value = { ...newValue };
      }
    });

    return {
      t,
      localData,
      options,
    };
  },
});
</script>

<style lang="scss" scoped>
.quit-site-form {
  display: flex;
  flex-direction: column;
  height: 100%;

  &__header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #e5e7eb;

    .p-button {
      width: 32px !important;
      height: 32px !important;
      min-width: 32px !important;
    }
  }

  &__title {
    flex: 1;

    h2 {
      margin: 0;
      font-size: 0.875rem;
      font-weight: 600;
      color: #1f2937;
    }
  }

  &__subtitle {
    font-size: 0.75rem;
    color: #6b7280;
  }

  &__content {
    flex: 1;
    padding: 1rem;
    overflow-y: auto;
  }

  &__field {
    margin-bottom: 1rem;
  }

  &__label {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
  }

  &__options {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__option {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: all 0.15s ease;

    i {
      font-size: 1rem;
      color: #6b7280;
    }

    span {
      font-size: 0.875rem;
      color: #374151;
    }

    &:hover {
      border-color: #3b82f6;
      background: #f0f9ff;
    }

    &--selected {
      border-color: #3b82f6;
      background: #eff6ff;

      i {
        color: #3b82f6;
      }

      span {
        color: #1d4ed8;
        font-weight: 500;
      }
    }
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    padding: 1rem;
    border-top: 1px solid #e5e7eb;
  }
}
</style>
